import { differenceInDays, endOfDay } from 'date-fns';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { getFormValues } from 'redux-form';

import { hideLoader, showLoader } from '@topconagriculture/loader';

import api from '../../../core/api';
import { LoadersNames } from '../../ui/ui.constants';
import { composeErrorMessage } from '../../../core/helpers/utils/utils';
import {
  createPreset,
  deletePreset,
  fetchPresetList,
  saveFeedingListPreset,
  saveAndApplyRefusalsPreset,
  updatePreset,
  saveAndApplyStockTransactionsPreset,
} from './preset.actions';
import { PresetSettings } from '../../../core/api/preset/preset.types';
import { DateRangeType, reportPreset } from '../../../config/constants';
import { compose403ErrorMessage, successHandler } from '../../../core/helpers/utils/sagaUtils';
import { notificationError, notificationSuccess } from '../../../core/helpers/utils/notificationUtils';
import { Forms } from '../../form/form.selector';
import { reportSettings as defaultReportSettings } from '../../../config/constants';
import { feedingActions, presetActions } from '../../actions';
import { settingsSelector } from '../../selectors';
import { getFeedingListPreset, getRefusalsPreset, getStockTransactionPreset } from './preset.selector';
import { getDaysBack, getRangeType } from '../../../shared/DateRangePicker/utils';
import { toLocalDate, toTimezoneDate } from '../../../core/helpers/utils/dateUtils';

const handleResult = (result) => {
  const movementTypesToString = (movementTypes) => movementTypes?.map((movementType) => movementType.toString());
  return Array.isArray(result)
    ? result.map((report) =>
        report.dataSettings.movementTypes
          ? {
              ...report,
              dataSettings: {
                ...report.dataSettings,
                movementTypes: movementTypesToString(report.dataSettings.movementTypes),
              },
            }
          : report,
      )
    : {
        ...result,
        dataSettings: {
          ...result.dataSettings,
          movementTypes: movementTypesToString(result.dataSettings.movementTypes),
        },
      };
};

export function* getPresetListSaga({ payload: { reportType } }: ReturnType<typeof fetchPresetList.started>) {
  yield put(showLoader(LoadersNames.fetchPreset));
  try {
    const result = yield call(api.preset.getPresetList, reportType);

    yield put(
      fetchPresetList.done({
        params: { reportType },
        result: handleResult(result),
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.fetchPreset));
}

export function* createPresetSaga({ payload, meta }: ReturnType<typeof createPreset.started>) {
  yield put(showLoader(LoadersNames.createPreset));
  try {
    const { data, formName } = payload;
    const daysBack = differenceInDays(data.to, data.from);
    const reportsPageForm = yield select(getFormValues(Forms.reportsPageForm));
    const { viewSettings, dataSettings, ...settings } = reportsPageForm?.settings || defaultReportSettings[data.type];
    const labelKey = reportsPageForm?.selectedTimeRange?.labelKey;
    const timezone = yield select(settingsSelector.getTimeZone);
    const preset: PresetSettings = {
      ...reportPreset[data.type],
      dataSettings: {
        ...settings,
        ...dataSettings,
        dateRangeType: getRangeType(labelKey),
        daysBack: getDaysBack(labelKey, daysBack),
        endDate: toLocalDate(toTimezoneDate(endOfDay(data.to), timezone)).toISOString(),
      },
      viewSettings,
    };
    const result = yield call(api.preset.createPreset, data.type, data.name, preset, formName);
    yield put(
      createPreset.done({
        params: payload,
        result,
      }),
    );
    successHandler(meta);
    yield put(notificationSuccess('reports:preset_create_success'));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.createPreset));
}

export function* deletePresetSaga({ payload, meta }: ReturnType<typeof deletePreset.started>) {
  try {
    const { data, formName } = payload;
    const result = yield call(api.preset.deletePreset, data.extra, data.id, formName);
    yield put(deletePreset.done({ params: payload, result }));
    successHandler(meta);
    yield put(notificationSuccess('reports:preset_delete_success'));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
}

export function* updatePresetSaga({ payload, meta }: ReturnType<typeof updatePreset.started>) {
  yield put(showLoader(LoadersNames.updateItem));
  const { data, formName } = payload;
  const { type, ...restProps } = data;
  const { viewSettings, ...dataSettings } = restProps.dataSettings;
  const timezone = yield select(settingsSelector.getTimeZone);
  const {
    timeRange: { endDate, labelKey, startDate },
  } = dataSettings;
  const daysBack = differenceInDays(endDate, startDate);

  const preset = {
    ...restProps,
    dataSettings: {
      ...dataSettings,
      dateRangeType: getRangeType(labelKey),
      daysBack: getDaysBack(labelKey, daysBack),
      endDate: toLocalDate(toTimezoneDate(endOfDay(endDate), timezone)).toISOString(),
      movementTypes: dataSettings.movementTypes
        ? dataSettings.movementTypes.map((movementType) => +movementType)
        : undefined,
    },
    viewSettings,
  };

  try {
    const result = yield call(api.preset.updatePreset, type, preset, formName);
    yield put(updatePreset.done({ params: payload, result: handleResult(result) }));
    successHandler(meta);
    yield put(notificationSuccess('reports:preset_update_success'));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }

  yield put(hideLoader(LoadersNames.updateItem));
}

function* getFeedingListPresetsSaga() {
  yield put(showLoader(LoadersNames.fetchPreset));
  try {
    const result = yield call(api.feeding.fetchPresetList);
    yield put(feedingActions.getFeedingListPresets.done({ params: {}, result }));
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
}

function* saveAndApplyFeedingListPresetSaga({ payload }) {
  yield put(showLoader(LoadersNames.fetchPreset));
  const { data, formName } = payload;

  try {
    const preset = yield select(getFeedingListPreset);
    const { reportType, kind, readOnly, ...restPresetFields } = preset;
    let result;

    if (preset.id) {
      result = yield call(api.preset.updateFeedingListPreset, { ...restPresetFields, ...data }, formName);
    } else {
      result = yield call(api.preset.createFeedingListPreset, { ...restPresetFields, ...data }, formName);
    }

    yield put(presetActions.updateFeedingListPreset(result));
  } catch (err) {
    yield compose403ErrorMessage(err);
  } finally {
    yield put(hideLoader(LoadersNames.fetchPreset));
  }
}

function* saveAndApplyRefusalsPresetSaga({ payload }) {
  const { formName } = payload;
  yield put(showLoader(LoadersNames.fetchRefusals));
  const formValue = yield select(getFormValues(Forms.refusalsSettingsForm));

  try {
    const preset = yield select(getRefusalsPreset);
    const { reportType, dataSettings, viewSettings, readOnly, ...rest } = preset;
    const presetToSave = {
      ...rest,
      // collapsed is value of radiobutton. this value is string and has to be transformed to boolean
      viewSettings: { ...formValue.viewSettings, collapsed: formValue.viewSettings.collapsed === 'true' },
      dataSettings: {
        daysBack: formValue.dataSettings.daysBack,
        endDate: formValue.dataSettings.endDate,
        includedGroups: formValue.includedGroups,
        includedGroupTypes: formValue.includedGroupTypes,
      },
    };

    let result;

    if (preset.id) {
      result = yield call(api.preset.updateRefusalsPreset, presetToSave, formName);
    } else {
      result = yield call(api.preset.createRefusalsPreset, presetToSave, formName);
    }
    yield put(presetActions.updateRefusalsPreset(result));
  } catch (err) {
    yield compose403ErrorMessage(err);
  } finally {
    yield put(hideLoader(LoadersNames.fetchRefusals));
  }
}

function* saveAndApplyStockTransactionsPresetSaga(
  action: ReturnType<typeof saveAndApplyStockTransactionsPreset.started>,
) {
  yield put(showLoader(LoadersNames.fetchPreset));
  const { formName } = action.payload;
  const formValue = yield select(getFormValues(Forms.stockTransactionSettingsForm));
  const stockTransactionsPreset = yield select(getStockTransactionPreset);
  try {
    const {
      reportType,
      dataSettings: presetDataSettings,
      viewSettings: presetViewSettings,
      readOnly,
      ...rest
    } = stockTransactionsPreset;
    const { dataSettings, viewSettings, ...restFormValues } = formValue;
    const dateRangeType = presetDataSettings.dateRangeType || DateRangeType.DAYS_BACK;
    const preset = {
      ...rest,
      viewSettings: { ...viewSettings, collapsed: viewSettings.collapsed === 'true' },
      dataSettings: {
        ...formValue.dataSettings,
        ...restFormValues,
        dateRangeType,
        daysBack: formValue.dataSettings.daysBack,
      },
    };

    let result;

    if (preset.id) {
      result = yield call(api.preset.updateStockTransactionsPreset, preset, formName);
    } else {
      result = yield call(api.preset.createStockTransactionsPreset, preset, formName);
    }

    yield put(
      saveAndApplyStockTransactionsPreset.done({
        result,
        params: action.payload,
      }),
    );

    successHandler(action.meta);
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
}

export function* watchPresetSaga() {
  yield all([
    yield takeLatest(fetchPresetList.started, getPresetListSaga),
    yield takeLatest(createPreset.started, createPresetSaga),
    yield takeLatest(deletePreset.started, deletePresetSaga),
    yield takeLatest(updatePreset.started, updatePresetSaga),
    yield takeLatest(feedingActions.getFeedingListPresets.started, getFeedingListPresetsSaga),
    yield takeLatest(saveFeedingListPreset.started, saveAndApplyFeedingListPresetSaga),
    yield takeLatest(saveAndApplyRefusalsPreset.started, saveAndApplyRefusalsPresetSaga),
    yield takeLatest(saveAndApplyStockTransactionsPreset.started, saveAndApplyStockTransactionsPresetSaga),
  ]);
}
